import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { invoiceSchema, noteSchema, totalsSchema } from './billing.api';

/**
 * Fișa clientului → Financiar, dinspre client
 * ([#186](https://github.com/bplugaru/efx-pcrm/issues/186)): facturile clientului și „Emite
 * factură", cu formularul precompletat.
 *
 * Schemele sunt scrise aici, nu importate din `@pcrm/be/billing`: `apps/crm` e `scope:fe`.
 */

/** Seria activă și numărul pe care îl va purta factura următoare: `0007`. */
const seriesSchema = z.object({ code: z.string(), nextNumber: z.string() });

/** Monedele unei linii. Factura e în lei; o linie în valută se convertește la emitere. */
export const LINE_CURRENCIES = ['RON', 'EUR', 'USD'] as const;

export type LineCurrency = (typeof LINE_CURRENCIES)[number];

const lineSchema = z.object({
  description: z.string(),
  unitCode: z.string(),
  quantity: z.string(),
  unitPrice: z.string(),
  currency: z.enum(LINE_CURRENCIES),
  priceIncludesVat: z.boolean(),
  /** Echipament, nu serviciu: decide codul CPV din e-Factura (#211). */
  isEquipment: z.boolean(),
});

/** O linie, cum o trimite formularul și cum o întoarce precompletarea. */
export type InvoiceLine = z.infer<typeof lineSchema>;

const customerInvoiceSchema = invoiceSchema.extend({ companyName: z.string() });

export type CustomerInvoice = z.infer<typeof customerInvoiceSchema>;

const customerBillingSchema = z.object({
  customerId: z.string(),
  company: z.object({
    id: z.string(),
    name: z.string(),
    shortName: z.string().nullable(),
    vatPayer: z.boolean(),
    series: seriesSchema.nullable(),
  }),
  detailedInvoice: z.boolean(),
  issueDate: z.string(),
  defaultDueDate: z.string(),
  vatRate: z.string().nullable(),
  exchangeRates: z.array(
    z.object({
      currency: z.string(),
      setting: z.string(),
      value: z.string().nullable(),
      date: z.string().nullable(),
      problem: z.string().nullable(),
    }),
  ),
  /** `'13:00'` cât cursul urmează să se schimbe azi, altfel `null` (#214). */
  exchangeRateUpdateAt: z.string().nullable().default(null),
  blockers: z.array(noteSchema),
  units: z.array(z.object({ code: z.string(), label: z.string() })),
  invoices: z.array(customerInvoiceSchema),
});

export type CustomerBillingView = z.infer<typeof customerBillingSchema>;

const prefillSchema = z.object({
  months: z.array(z.string()),
  monthsLabel: z.string(),
  blockers: z.array(noteSchema),
  invoices: z.array(invoiceSchema),
  lines: z.array(lineSchema),
  reasons: z.array(noteSchema),
  markers: z.array(noteSchema),
});

export type MonthlyPrefill = z.infer<typeof prefillSchema>;

const previewSchema = z.object({
  html: z.string(),
  totals: totalsSchema,
  fingerprint: z.string(),
  issueDate: z.string(),
  dueDate: z.string(),
  series: z.string(),
  number: z.string(),
});

export type ManualInvoicePreview = z.infer<typeof previewSchema>;

const invoiceFormSchema = z.object({
  invoiceId: z.string(),
  series: z.string(),
  number: z.string(),
  kind: z.enum(['monthly', 'free']),
  months: z.array(z.string()),
  issueDate: z.string(),
  dueDate: z.string(),
  lines: z.array(lineSchema),
  notes: z.array(z.string()),
});

/** Corectura unei facturi netrimise (#201): formularul, precompletat din factură. */
export type InvoiceFormView = z.infer<typeof invoiceFormSchema>;

/** Ce trimite formularul: tipul facturii, lunile (doar la abonament), datele, liniile. */
export interface ManualInvoiceRequest {
  readonly kind: 'monthly' | 'free';
  /** Lunile facturate, în ordine (#210); goală la echipamente și servicii. */
  readonly months: readonly string[];
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly lines: readonly InvoiceLine[];
}

export const customerBillingQueryKey = (customerId: string) =>
  ['billing', 'customer', customerId] as const;

/** Cât de des se reîncarcă lista cât se fac fișierele unei facturi tocmai emise. */
const FILES_REFRESH_MS = 3_000;

/**
 * Tabul Financiar. Se reîncarcă singur **doar cât fișierele unei facturi sunt pe drum**: PDF-ul și
 * XML-ul se fac după emitere, în câteva secunde.
 */
export const customerBillingQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: customerBillingQueryKey(customerId),
    queryFn: () =>
      apiGet(`/customers/${encodeURIComponent(customerId)}/billing`, customerBillingSchema),
    refetchInterval: (query) =>
      (query.state.data?.invoices ?? []).some(
        (invoice) => (!invoice.files.pdf || !invoice.files.xml) && invoice.files.error === null,
      )
        ? FILES_REFRESH_MS
        : false,
  });

/** Liniile pe lunile alese, câte un rând de linii pe lună (#210). */
export function prefillMonthlyInvoice(
  customerId: string,
  months: readonly string[],
): Promise<MonthlyPrefill> {
  return apiGet(
    `/customers/${encodeURIComponent(customerId)}/billing/prefill?months=${encodeURIComponent(months.join(','))}`,
    prefillSchema,
  );
}

/** Previzualizarea unei facturi noi sau, cu `invoiceId`, a corecturii ei. */
export function previewManualInvoice(
  customerId: string,
  request: ManualInvoiceRequest,
  invoiceId?: string,
): Promise<ManualInvoicePreview> {
  const base = `/customers/${encodeURIComponent(customerId)}/billing`;

  return apiPost(
    invoiceId === undefined
      ? `${base}/preview`
      : `${base}/invoices/${encodeURIComponent(invoiceId)}/preview`,
    request,
    previewSchema,
  );
}

export function loadInvoiceForm(customerId: string, invoiceId: string): Promise<InvoiceFormView> {
  return apiGet(
    `/customers/${encodeURIComponent(customerId)}/billing/invoices/${encodeURIComponent(invoiceId)}`,
    invoiceFormSchema,
  );
}

/** Corectura confirmată: aceeași factură, același număr, fișierele refăcute. */
export function updateManualInvoice(
  customerId: string,
  invoiceId: string,
  request: ManualInvoiceRequest & { readonly fingerprint: string },
) {
  return apiPut(
    `/customers/${encodeURIComponent(customerId)}/billing/invoices/${encodeURIComponent(invoiceId)}`,
    request,
    z.object({ invoice: invoiceSchema }),
  ).then((body) => body.invoice);
}

export function issueManualInvoice(
  customerId: string,
  request: ManualInvoiceRequest & { readonly fingerprint: string },
) {
  return apiPost(
    `/customers/${encodeURIComponent(customerId)}/billing/invoices`,
    request,
    z.object({ invoice: invoiceSchema }),
  ).then((body) => body.invoice);
}
